import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation
} from "@angular/core";
import {Api, ApiService} from "../../../../../../common/services/api.service";
import {Api3Service} from "../../../../../../common/services/api3.service";
import {SpinnerService} from "../../../../../../common/services/spinner.service";
import {ToastService} from "../../../../../../common/services/toast.service";
import {Router} from "@angular/router";
import {Base} from "../../../../../../common/interfaces/base.interfaces";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {takeUntil} from "rxjs/operators";
import {ConfirmComponent} from "../../../../../../common/components/confirm/confirm.component";
import {CommonFormComponent} from "../../../../../../common/components/form";
import {Modal, ModalService} from "../../../../../services/modal.service";
import {DragulaService} from "ng2-dragula";
import {Subscription} from "rxjs";
import {ELEMENTS_LIST} from "../renderer/label-elements";
import {gridTile} from "../renderer/label-renderer.component";
import {HelpersService} from "../../../../../../common/services/helpers.service";
import {PartnerService} from "../../../../../../common/services/partner.service";
import {WarehouseService} from "../../../../../../common/services/warehouse.service";
import {AlertComponent} from "../../../../../../common/components/alert/alert.component";
import {UserService} from "../../../../../../common/services/user.service";

export interface ILabelSize {
    id: number;
    name: string;
    width: number;
    height: number;
}


@Component({
    selector: "section-custom-label-view",
    templateUrl: "view.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    styleUrls: ["view.component.scss"]
})
export class CustomLabelViewComponent implements OnInit, OnDestroy {

    private destroy$: EventEmitter<boolean> = new EventEmitter(false);

    private dragSubscription = new Subscription();

    public state: Base.IState;

    public labelSizes: ILabelSize[] = [];

    public owned: boolean = false;

    public formGroup: FormGroup = new FormGroup({
        name: new FormControl(null, [Validators.required]),
        type: new FormControl("text", [Validators.required]),
        size: new FormGroup({
            id: new FormControl(null),
            name: new FormControl(null, [Validators.required]),
            width: new FormControl(null, [Validators.required, Validators.min(10), Validators.max(500)]),
            height: new FormControl(null, [Validators.required, Validators.min(0)]),
        }),
        body: new FormControl(""),
        layout: new FormControl([]),
        is_template: new FormControl({value: false, disabled: true}),
        sorting_order: new FormControl(1, [Validators.required, Validators.min(1)]),
    });

    @ViewChild("sticker", {static: false})
    public sticker: ElementRef;

    @ViewChild(ConfirmComponent, {static: true})
    public confirm: ConfirmComponent;

    @ViewChild(AlertComponent, {static: true})
    public alert: AlertComponent;

    public editorInitialValue: string = null;

    public stickerElements: any[] = [];

    public tiles: gridTile[] = [
        {
            size: "100%",
            align_h: "center",
            align_v: "top",
            border_t: false,
            border_r: false,
            border_b: false,
            border_l: false,
            color: this.generateRandomLightColor(),
            content: []
        },
    ];

    public stickerLayout: gridTile[] = [];

    public stickerData: any = {};

    public constructor(
        private apiService: ApiService,
        private api3Service: Api3Service,
        private spinnerService: SpinnerService,
        private toastService: ToastService,
        private router: Router,
        private changeDetectorRef: ChangeDetectorRef,
        private modalService: ModalService,
        private dragulaService: DragulaService,
        private userService: UserService
    ) {
        if (!dragulaService.find("TILES")) {
            dragulaService.createGroup("TILES", {
                moves: (el, container, handle) => {
                    return handle.className ===
                        "mat-icon notranslate tile-drag-handle material-icons mat-ligature-font mat-icon-no-color";
                }
            });
        }

        this.dragSubscription.add(dragulaService.dropModel("ITEMS")
            .subscribe(({el, target, source, sourceModel, targetModel, item}) => {
                setTimeout(() => {
                    this.stickerLayout = [...this.tiles];
                    this.changeDetectorRef.markForCheck();
                }, 10);
            }));

        this.dragSubscription.add(dragulaService.dropModel("TILES")
            .subscribe(({el, target, source, sourceModel, targetModel, item}) => {
                setTimeout(() => {
                    this.stickerLayout = [...this.tiles];
                    this.changeDetectorRef.markForCheck();
                }, 10);
            }));
    }

    private async getLabel(): Promise<any> {
        this.spinnerService.show();
        const {data}: Api.IResponse = await this.api3Service.get(
            `${this.state.section}/custom-labels/${this.state.params.id}`);

        this.spinnerService.hide();

        if (data) {
            if (this.state.action === "add") {
                this.owned = true;
                data.id = null;
                data.name = null;
                this.formGroup.get("is_template").enable();
                data.is_template = false;
            } else if (this.userService.validatePermissions(["super_admin"])) {
                this.owned = true;
            } else if (data.is_template && this.userService.validatePermissions(["templates_custom_labels"])) {
                this.owned = true;
            } else if (PartnerService.partner?.id === data.partner_id) {
                this.owned = true;
            } else if (WarehouseService.warehouse?.id === data.warehouse_id) {
                this.owned = true;
            }

            this.formGroup.patchValue(data);
            this.editorInitialValue = data.body;

            this.tiles = [];

            data.layout.forEach((item: gridTile) => {
                item.color = this.generateRandomLightColor();
                this.tiles.push(item);
            });

            this.setElementsList(this.formGroup.get("type").value);

            this.changeDetectorRef.markForCheck();
        }
    }

    private async getSizes(): Promise<any> {
        this.spinnerService.show();
        const {data}: Api.IResponse = await this.api3Service.get(`${this.state.section}/custom-labels/sizes`);
        this.spinnerService.hide();
        if (data) {
            this.labelSizes = data;
            this.changeDetectorRef.markForCheck();
        }
    }

    private setElementsList(type: string): void {
        const type_tags: string[] = [];

        this.stickerElements = ELEMENTS_LIST.filter((item: any) => {
            return item.types.includes(type);
        })
            .map((item: any): { name: string, tag: string } => {
                type_tags.push(item.tag);

                return {
                    name: item.name,
                    tag: item.tag,
                };
            });

        let used_tags: string[] = [];

        for (const tile of this.tiles) {
            tile.content = tile.content.filter((item: { tag: string }): boolean => {
                return type_tags.includes(item.tag);
            });

            for (const content of tile.content) {
                used_tags.push(content.tag);
            }
        }

        this.stickerElements = this.stickerElements.filter((item: any): boolean => {
            return !used_tags.includes(item.tag);
        });

        this.stickerLayout = [...this.tiles];

        this.changeDetectorRef.markForCheck();
    }


    public print(): void {
        if (!this.sticker?.nativeElement) {
            return;
        }

        const stickerElement: HTMLElement = this.sticker.nativeElement.cloneNode(true) as HTMLElement;
        stickerElement.classList.remove("outline");

        const tilesContainer: HTMLElement | null = stickerElement.querySelector(".tiles-container");
        if (tilesContainer) {
            tilesContainer.style.overflow = "visible";
            tilesContainer.style.height = "auto";
        }

        HelpersService.stickerPopUp(stickerElement.innerHTML);
    }

    public getSelectedSize(id: number): any {
        if (!id) {
            return null;
        }
        return this.labelSizes.find((size: ILabelSize) => {
            return size.id === id;
        });
    }

    public showSizeString(size: ILabelSize): string {
        if (!size) {
            return "";
        }
        return `${size.name ?? ""} (${size.width ?? 0} mm x ${size.height > 0 ? size.height + " mm" : "auto"})`;
    }

    public async deleteSize(id: number): Promise<any> {
        if (!await this.confirm.confirm("Are you sure want to delete this size? This action cannot be undone!")) {
            return;
        }
        this.spinnerService.show();

        const {message, code}: Api.IResponse = await this.apiService.request(Api.EMethod.Delete,
            ["custom_labels", "sizes", "" + id]);

        this.spinnerService.hide();
        if (code === 200) {
            this.toastService.show(message, "success");
            this.getSizes();
        }
    }

    public async saveLabel(): Promise<any> {
        this.spinnerService.show();
        let res: Api.IResponse;

        this.formGroup.get("layout").setValue(this.tiles.map((tile: gridTile) => {
            return {
                size: tile.size,
                align_h: tile.align_h,
                align_v: tile.align_v,
                border_t: tile.border_t,
                border_r: tile.border_r,
                border_b: tile.border_b,
                border_l: tile.border_l,
                content: tile.content,
            };
        }));


        if (this.state.action === "edit") {
            res = await this.api3Service.put(
                `${this.state.section}/custom-labels/${this.state.params.id}`,
                this.formGroup.value);
        } else if (this.state.action === "add") {
            res = await this.api3Service.post(
                `${this.state.section}/custom-labels`,
                this.formGroup.value);
        } else {
            this.alert.show("Unknown action");
            this.spinnerService.hide();
            return;
        }

        this.spinnerService.hide();
        if (res.data) {
            this.toastService.show(res.message, "success");
            this.router.navigate([
                this.state.section,
                this.state.component,
                "edit",
                "id",
                res.data.id
            ]);
        }
    }

    public async deleteLabel(): Promise<any> {
        if (!await this.confirm.confirm("Are you sure want to delete this label? This action cannot be undone!")) {
            return;
        }

        this.spinnerService.show();

        const {message, code}: Api.IResponse = await this.api3Service.delete(
            `${this.state.section}/custom-labels/${this.state.params.id}`,
        );

        this.spinnerService.hide();
        if (code === 200) {
            this.toastService.show(message, "success");
            this.goToList();
        }
    }

    public goToList(): void {
        this.router.navigate([
            this.state.section,
            this.state.component,
        ]);
    }

    public addTile(): void {
        this.tiles.push({
            size: "100%",
            color: this.generateRandomLightColor(),
            content: [],
            align_h: "center",
            align_v: "top",
            border_t: false,
            border_r: false,
            border_b: false,
            border_l: false,
        });

        this.stickerLayout = [...this.tiles];
        this.changeDetectorRef.markForCheck();
    }

    public async editTile(tile: gridTile): Promise<any> {
        const res: Modal.IResponse = await this.modalService.open(CommonFormComponent, {
            formConfig: {
                name: "Edit block",
                description: "",
                fields: [
                    {
                        label: "Block width",
                        name: "size",
                        size: "third",
                        type: "select",
                        values: [
                            "100%", "75%", "50%", "25%"
                        ],
                        selected_on_top: false,
                        required: true,
                    },
                    {
                        label: "Content horizontal align",
                        name: "align_h",
                        size: "third",
                        type: "select",
                        values: [
                            "left", "center", "right", "stretch"
                        ],
                        selected_on_top: false,
                        required: true,
                    },
                    {
                        label: "Content vertical align",
                        name: "align_v",
                        size: "third",
                        type: "select",
                        values: [
                            "top", "middle", "bottom"
                        ],
                        selected_on_top: false,
                        required: true,
                    },
                    {
                        label: "Top border",
                        name: "border_t",
                        size: "half",
                        type: "checkbox",
                        values: false,
                    },
                    {
                        label: "Right border",
                        name: "border_r",
                        size: "half",
                        type: "checkbox",
                        values: false,
                    },
                    {
                        label: "Bottom border",
                        name: "border_b",
                        size: "half",
                        type: "checkbox",
                        values: false,
                    },
                    {
                        label: "Left border",
                        name: "border_l",
                        size: "half",
                        type: "checkbox",
                        values: false,
                    },
                ],
            },
            values: tile,
            noReset: true,
        });

        if (res && res.value) {
            tile.size = res.value.size;
            tile.align_h = res.value.align_h;
            tile.align_v = res.value.align_v;
            tile.border_t = res.value.border_t;
            tile.border_b = res.value.border_b;
            tile.border_l = res.value.border_l;
            tile.border_r = res.value.border_r;
        }

        this.stickerLayout = [...this.tiles];
        this.changeDetectorRef.markForCheck();

    }

    public removeTile(index: number): void {
        if (this.tiles[index].content.length > 0) {
            this.alert.show("Block is not empty!");
            return;
        }

        this.tiles.splice(index, 1);
        this.stickerLayout = [...this.tiles];
        this.changeDetectorRef.markForCheck();
    }

    public setHorizontalAlign(align: string): string {
        switch (align) {
            case "left":
                return "flex-start";
            case "right":
                return "flex-end";
            case "stretch":
                return "stretch";
            default:
                return "center";
        }
    }

    public setVerticalAlign(align: string): string {
        switch (align) {
            case "bottom":
                return "flex-end";
            case "middle":
                return "center";
            case "top":
            default:
                return "flex-start";
        }
    }

    public generateRandomLightColor(): string {
        const r: number = Math.floor(Math.random() * 106) + 150;
        const g: number = Math.floor(Math.random() * 106) + 150;
        const b: number = Math.floor(Math.random() * 106) + 150;

        return `rgb(${r}, ${g}, ${b})`;
    }

    public ngOnInit(): void {
        if (this.state.params.id) {
            this.getLabel();
        } else if (this.state.action === "add") {
            this.owned = true;
            this.formGroup.get("is_template").enable();
        }

        this.getSizes();

        this.stickerData = ELEMENTS_LIST.filter((item: any) => {
            return !!item.sample;
        }).reduce((result, item) => {
            result[item.tag] = item.sample;
            return result;
        }, {});

        this.setElementsList(this.formGroup.get("type").value);

        this.formGroup.get("size").get("id").valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe((value: number): void => {
                const size: ILabelSize = this.getSelectedSize(value);
                if (size) {
                    this.formGroup.get("size").get("name").setValue(size.name);
                    this.formGroup.get("size").get("width").setValue(size.width);
                    this.formGroup.get("size").get("height").setValue(size.height);
                    this.changeDetectorRef.markForCheck();
                }
            });

        this.formGroup.get("type").valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: string): void => {
            this.setElementsList(value);
        });

        this.formGroup.get("body").valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: string): void => {
            this.stickerData.free_text = value;

            this.stickerLayout = [...this.tiles];
        });
    }

    public ngConfig(): Base.IConfig {
        return {
            name: "custom-labels",
            actions: {
                "edit": ["edit_custom_labels", "browse_custom_labels"],
                "add": ["add_custom_labels"],
            }
        };
    }

    public ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();

        this.dragSubscription.unsubscribe();
    }
}
